import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { updateTheme as saveThemeToServer } from '../services/userService';

type Theme = 'dark' | 'light';

/** Where the new theme grows from: the pressed control, or its click event. */
type ThemeOrigin = Element | React.SyntheticEvent | Event | null | undefined;

interface ThemeContextType {
    theme: Theme;
    toggleTheme: (origin?: ThemeOrigin) => void;
}

type ViewTransitionDocument = Document & { startViewTransition?: (update: () => void) => unknown };

const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Centre of the control the switch came from; the middle of the screen otherwise.
function originPoint(origin: ThemeOrigin): { x: number; y: number } {
    const el = origin instanceof Element
        ? origin
        : origin && 'currentTarget' in origin && origin.currentTarget instanceof Element
            ? origin.currentTarget
            : null;
    if (!el) return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Applies the theme class to <html> and persists it locally
function applyTheme(theme: Theme) {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
}

// Owns the light/dark theme, synced with the server profile
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userProfile, isAuthenticated } = useAuth();

    // Initialize from localStorage, defaulting to 'light' (no system preference fallback)
    const [theme, setTheme] = useState<Theme>(() => {
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme') as Theme;
            if (savedTheme === 'dark' || savedTheme === 'light') {
                return savedTheme;
            }
        }
        return 'light';
    });

    // When user profile loads, sync theme from server (server is source of truth)
    useEffect(() => {
        if (userProfile) {
            const serverTheme = userProfile.theme;
            if (serverTheme === 'dark' || serverTheme === 'light') {
                setTheme(serverTheme);
            } else {
                // No theme set on server yet — default to light and persist it
                setTheme('light');
                saveThemeToServer('light').catch((err) => console.error('Failed to save theme:', err));
            }
        }
    }, [userProfile?.theme]);

    // When user logs out, reset to light
    useEffect(() => {
        if (!isAuthenticated) {
            setTheme('light');
        }
    }, [isAuthenticated]);

    // Apply theme to DOM whenever it changes
    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    // The new theme grows as a circle from the control that was pressed (#410),
    // where the browser has view transitions and motion is welcome.
    const toggleTheme = useCallback((origin?: ThemeOrigin) => {
        const next = theme === 'light' ? 'dark' : 'light';
        const doc = document as ViewTransitionDocument;
        if (typeof doc.startViewTransition === 'function' && !prefersReducedMotion()) {
            const { x, y } = originPoint(origin);
            const root = document.documentElement.style;
            root.setProperty('--theme-x', `${x}px`);
            root.setProperty('--theme-y', `${y}px`);
            root.setProperty(
                '--theme-r',
                `${Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))}px`
            );
            // The snapshot needs the new theme in the DOM before the callback returns.
            doc.startViewTransition(() => {
                applyTheme(next);
                flushSync(() => setTheme(next));
            });
        } else {
            setTheme(next);
        }
        saveThemeToServer(next).catch((err) => console.error('Failed to save theme:', err));
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
